import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const BookNowPage: React.FC = () => {
  const [formData, setFormData] = useState({
    residency: '',
    isChildPsychiatric: '',
    nhsNumber: '',
    serviceType: '',
    sessionDuration: '50',
    specificService: '',
    customService: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const serviceOptions = {
    therapy: [
      'Cognitive behavioural therapy (CBT)',
      'Counselling',
      'Integrated therapy',
      'Clinical or counselling psychology',
      'Employee Assistance Programme (EAP)',
      'Couple therapy',
      'Family therapy'
    ],
    supervision: [
      'Clinical Supervision (specific modality/mental health field)',
      'Professional Mentorship',
      'Professional Accreditation Advisory Service'
    ],
    mentoring: [
      'Youth Mentoring',
      'Children Mentoring Network'
    ],
    coaching: [
      'Staff reflective practice to learn',
      'Staff wellbeing emotional support',
      'Psychological coaching',
      'Staff and organisational wellbeing strategic consultations and interventions at senior managers and board levels',
      'Work place Mediations'
    ],
    other: [
      'Family Financial Wellbeing',
      'Not sure (contact us and allow us to guide you)',
      'Not on this list (specify below)'
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-slate-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Book Your Session</h1>
          <p className="text-xl text-gray-300">Professional mental health services tailored to your needs</p>
        </div>
      </div>

      {/* Booking Form */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-slate-700 mb-8">Booking Information</h2>

          {/* Residency Status */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Where are you resident? *
            </label>
            <div className="space-y-3">
              {['United Kingdom', 'Overseas/Non-United Kingdom', 'ExPat'].map((option) => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="residency"
                    value={option}
                    checked={formData.residency === option}
                    onChange={handleInputChange}
                    className="mr-3 h-4 w-4 text-slate-600"
                    required
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Child & Adolescent or Psychiatric Service */}
          {formData.residency === 'United Kingdom' && (
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-3">
                Is this booking for a child & adolescent or a psychiatric service? *
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="isChildPsychiatric"
                    value="no"
                    checked={formData.isChildPsychiatric === 'no'}
                    onChange={handleInputChange}
                    className="mr-3 h-4 w-4 text-slate-600"
                    required
                  />
                  <span className="text-gray-700">No</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="isChildPsychiatric"
                    value="yes"
                    checked={formData.isChildPsychiatric === 'yes'}
                    onChange={handleInputChange}
                    className="mr-3 h-4 w-4 text-slate-600"
                    required
                  />
                  <span className="text-gray-700">Yes (NHS number required)</span>
                </label>
              </div>
            </div>
          )}

          {/* NHS Number */}
          {formData.isChildPsychiatric === 'yes' && (
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                NHS Number *
              </label>
              <input
                type="text"
                name="nhsNumber"
                value={formData.nhsNumber}
                onChange={handleInputChange}
                placeholder="Enter your NHS number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                required
              />
            </div>
          )}

          {/* Service Type */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Select Service Type *
            </label>
            <select
              name="serviceType"
              value={formData.serviceType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
              required
            >
              <option value="">Choose a service category...</option>
              <option value="therapy">Therapy, Counselling, Talking Treatments or Coaching</option>
              <option value="supervision">Supervision, Mentorship and Accreditation</option>
              <option value="mentoring">Children Mentoring</option>
              <option value="coaching">Coaching and Staff Wellbeing Services</option>
              <option value="other">Other Services</option>
            </select>
          </div>

          {/* Specific Service */}
          {formData.serviceType && (
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Select Specific Service *
              </label>
              <select
                name="specificService"
                value={formData.specificService}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                required
              >
                <option value="">Choose a specific service...</option>
                {serviceOptions[formData.serviceType as keyof typeof serviceOptions]?.map((service, index) => (
                  <option key={index} value={service}>{service}</option>
                ))}
              </select>
            </div>
          )}

          {/* Custom Service Input */}
          {formData.specificService === 'Not on this list (specify below)' && (
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Please specify the service you need *
              </label>
              <textarea
                name="customService"
                value={formData.customService}
                onChange={handleInputChange}
                rows={3}
                placeholder="Describe the service you're looking for..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                required
              />
            </div>
          )}

          {/* Session Duration */}
          {formData.serviceType === 'therapy' && (
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-3">
                Session Duration *
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sessionDuration"
                    value="25"
                    checked={formData.sessionDuration === '25'}
                    onChange={handleInputChange}
                    className="mr-3 h-4 w-4 text-slate-600"
                  />
                  <span className="text-gray-700">25 minutes (half fee)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sessionDuration"
                    value="50"
                    checked={formData.sessionDuration === '50'}
                    onChange={handleInputChange}
                    className="mr-3 h-4 w-4 text-slate-600"
                  />
                  <span className="text-gray-700">50 minutes (full fee)</span>
                </label>
              </div>
            </div>
          )}

          {/* Pricing Information */}
          <div className="mb-8 p-4 bg-slate-50 rounded-lg">
            <h3 className="text-lg font-bold text-slate-700 mb-2">Pricing Information</h3>
            <p className="text-gray-600 text-sm mb-2">
              Session fees vary by service type and duration. Reports and letters maintain standard CBT website pricing.
            </p>
            <p className="text-gray-600 text-sm">
              For detailed pricing information, please visit our{' '}
              <a 
                href="https://cognitivebehaviouraltherapy.healthcity.org.uk/booking" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-slate-800 underline"
              >
                CBT booking page
              </a>
              .
            </p>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              className="bg-slate-700 text-white px-8 py-3 rounded-lg hover:bg-slate-600 transition-colors duration-200 font-semibold"
            >
              Proceed to Payment
            </button>
          </div>
        </form>

        {/* Additional Information */}
        <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-xl font-bold text-slate-700 mb-4">Need Help?</h3>
          <p className="text-gray-600 mb-4">
            If you're unsure about which service is right for you, or if you need assistance with booking, 
            please don't hesitate to contact us.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-slate-100 text-slate-700 px-6 py-2 rounded-lg hover:bg-slate-200 transition-colors duration-200"
          >
            Contact Us for Guidance
          </Link>
        </div>

        {/* Testing Features and Terms Section */}
        <div className="mt-12 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-slate-700 px-8 py-4">
            <h3 className="text-xl font-bold text-white">Additional Information & Terms</h3>
          </div>
          
          <div className="p-8 space-y-8">
            {/* Testing Features */}
            <div className="bg-slate-50 rounded-lg p-6 border-l-4 border-green-500">
              <p className="text-slate-800 font-semibold text-lg mb-4">
                Would you be interested in helping us test new features?
              </p>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer group">
                  <input 
                    type="radio" 
                    name="testingInterest" 
                    value="yes" 
                    className="mr-3 w-5 h-5 text-green-600 focus:ring-green-500 cursor-pointer" 
                  />
                  <span className="text-slate-700 font-medium group-hover:text-green-600 transition-colors">
                    Yes, I'm interested
                  </span>
                </label>
                <label className="flex items-center cursor-pointer group">
                  <input 
                    type="radio" 
                    name="testingInterest" 
                    value="no" 
                    className="mr-3 w-5 h-5 text-slate-600 focus:ring-slate-500 cursor-pointer" 
                  />
                  <span className="text-slate-700 font-medium group-hover:text-slate-600 transition-colors">
                    No, thank you
                  </span>
                </label>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="space-y-6">
              <h4 className="text-lg font-bold text-slate-800 border-b-2 border-slate-200 pb-2">
                Terms & Conditions
              </h4>
              
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mt-1">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <p>
                    I agree that if my appointment overruns then I may be charged for extra consultation minutes. 
                    This is, however, at the discretion of the psychiatrist and you will be informed at the end of 
                    your appointment if any extra charges are applicable.
                  </p>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mt-1">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <p>
                    By booking an appointment with you, I accept that you need to use my personal data to set me up 
                    as a patient and perform our contract for services with me. I understand that this consultation 
                    will be conducted online and an audio recording will be made. I have read your{' '}
                    <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 underline font-medium">
                      Privacy Policy Statement
                    </Link>{' '}
                    and agree to your{' '}
                    <Link to="/terms-and-conditions" className="text-blue-600 hover:text-blue-800 underline font-medium">
                      Terms and Conditions
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookNowPage;